import { useState } from "react";
import BulkUploadButton from "../../components/BulkUploadButton";
import DataExportButton from "../../components/DataExportButton";
import {
  Alert,
  Box,
  Button,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { LocalShippingOutlined, CheckCircleOutline, PauseCircleOutline, LocationCityOutlined } from "@mui/icons-material";
import { SparePageHeader, SpareSummary, sparePageSx } from "../../components/SparePagePresentation";

import {
  AppDeleteDialog,
  AppSearchField,
  AppToolbar,
} from "../../../../components/common";

import SupplierFormDialog from "../components/SupplierFormDialog";
import SupplierTable from "../components/SupplierTable";

import { useSuppliers } from "../hooks/useSuppliers";

import {
  Supplier,
  SupplierPayload,
} from "../types";
import { filterRowsBySearch } from "../../utils/rowSearch";

const SuppliersPage = () => {
  const {
    suppliers,
    loading,
    error,
    pagination,
    filters,
    fetchSuppliers,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    updateStatus,
    updateFilters,
  } = useSuppliers();

  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);

  const [selectedSupplier, setSelectedSupplier] =
    useState<Supplier | null>(null);

  const [saving, setSaving] = useState(false);

  const [deleteOpen, setDeleteOpen] =
    useState(false);

  const [deleteLoading, setDeleteLoading] =
    useState(false);

  const [supplierToDelete, setSupplierToDelete] =
    useState<Supplier | null>(null);

  const handleCreate = () => {
    setSelectedSupplier(null);
    setDialogOpen(true);
  };

  const handleEdit = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedSupplier(null);
  };

  const handleSubmit = async (
    payload: SupplierPayload
  ) => {
    try {
      setSaving(true);

      if (selectedSupplier) {
        await updateSupplier(
          selectedSupplier.id,
          payload
        );
      } else {
        await createSupplier(payload);
      }

      handleClose();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (
    supplier: Supplier
  ) => {
    setSupplierToDelete(supplier);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!supplierToDelete) return;

    try {
      setDeleteLoading(true);

      await deleteSupplier(
        supplierToDelete.id
      );

      setDeleteOpen(false);
      setSupplierToDelete(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSearchChange = (
    value: string
  ) => {
    setSearch(value);

    updateFilters({
      search: value,
      page: 1,
    });
  };

  return (
    <Box
      sx={sparePageSx("suppliers")}
    >
      <Stack spacing={3}>
      <SparePageHeader kind="suppliers"
          title="Suppliers"
          subtitle="Your supply network. Contacts, locations, and trusted spare part partners."
          action={
            <Stack direction="row" flexWrap="wrap" gap={1}>
            <BulkUploadButton kind="suppliers" onImported={fetchSuppliers} />
            <DataExportButton kind="suppliers" filters={filters} disabled={loading} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              Create Supplier
            </Button>
            </Stack>
          }
        />

        {error && (
          <Alert
            severity="error"
            onClose={fetchSuppliers}
          >
            {error}
          </Alert>
        )}

        <SpareSummary items={[
          { label: "Total Suppliers", value: loading ? "—" : pagination.total, icon: <LocalShippingOutlined />, color: "#0d9488" },
          { label: "Active · this page", value: loading ? "—" : suppliers.filter((item) => item.status === "ACTIVE").length, icon: <CheckCircleOutline />, color: "#10b981" },
          { label: "Inactive · this page", value: loading ? "—" : suppliers.filter((item) => item.status === "INACTIVE").length, icon: <PauseCircleOutline />, color: "#64748b" },
          { label: "Cities · this page", value: loading ? "—" : new Set(suppliers.map((item) => item.city).filter(Boolean)).size, icon: <LocationCityOutlined />, color: "#0284c7" },
        ]} />
        <AppToolbar
          left={
            <AppSearchField
              value={search}
              fullWidth
              placeholder="Search supplier..."
              onChange={
                handleSearchChange
              }
            />
          }
        />

        <SupplierTable
          rows={filterRowsBySearch(suppliers, search)}
          loading={loading}
          rowCount={pagination.total}
          page={filters.page - 1}
          pageSize={filters.limit}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onStatusChange={(
            supplier,
            checked
          ) =>
            updateStatus(
              supplier.id,
              checked
                ? "ACTIVE"
                : "INACTIVE"
            )
          }
          onPageChange={(page) =>
            updateFilters({
              page: page + 1,
            })
          }
          onPageSizeChange={(
            pageSize
          ) =>
            updateFilters({
              page: 1,
              limit: pageSize,
            })
          }
        />
      </Stack>

      <SupplierFormDialog
        open={dialogOpen}
        loading={saving}
        supplier={selectedSupplier}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />

      <AppDeleteDialog
        open={deleteOpen}
        loading={deleteLoading}
        title="Delete Supplier"
        content={`Are you sure you want to delete ${
          supplierToDelete?.name ??
          "this supplier"
        } ?`}
        onClose={() => {
          setDeleteOpen(false);
          setSupplierToDelete(null);
        }}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default SuppliersPage;
